import '../css/index.css';
import '../css/components/shell.css';
import '../css/components/home.css';
import '../css/components/card.css';
import '../css/components/article.css';
import '../css/components/page.css';
import '../css/components/mobile.css';
import '../css/components/states.css';
import pinyinFirstLetter from 'pinyin-firstletter/pinyin_dict_firstletter.js';

const $ = (selector, scope = document) => scope.querySelector(selector);
const $$ = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));
const i18n = $('[data-theme-i18n]')?.dataset || {};
const translated = (key, fallback) => i18n[key] || fallback;

function initSheets() {
    const backdrop = $('.sheet-backdrop');
    let previousFocus = null;
    const closeAll = () => {
        $$('.mobile-sheet.is-open').forEach((sheet) => {
            sheet.classList.remove('is-open');
            sheet.setAttribute('aria-hidden', 'true');
        });
        backdrop?.classList.remove('is-open');
        document.body.classList.remove('sheet-open');
        previousFocus?.focus?.();
        previousFocus = null;
    };

    $$('[data-sheet-open]').forEach((trigger) => {
        trigger.addEventListener('click', () => {
            const sheet = document.getElementById(trigger.dataset.sheetOpen);
            if (!sheet) return;
            sheet.classList.add('is-open');
            sheet.setAttribute('aria-hidden', 'false');
            backdrop?.classList.add('is-open');
            document.body.classList.add('sheet-open');
            previousFocus = trigger;
            ($('[data-sheet-close]', sheet) || $('.site-brand', sheet))?.focus();
        });
    });
    $$('[data-sheet-close]').forEach((button) => button.addEventListener('click', closeAll));
    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') closeAll();
    });

    $$('.mobile-sheet').forEach((sheet) => {
        let startY = 0;
        let deltaY = 0;
        sheet.addEventListener('pointerdown', (event) => {
            if (!event.target.closest('.sheet-handle,.sheet-head')) return;
            startY = event.clientY;
            deltaY = 0;
            sheet.setPointerCapture?.(event.pointerId);
        });
        sheet.addEventListener('pointermove', (event) => {
            if (!startY) return;
            deltaY = Math.max(0, event.clientY - startY);
            sheet.style.transform = `translate(-50%, ${deltaY}px)`;
        });
        const finish = () => {
            if (!startY) return;
            sheet.style.transform = '';
            if (deltaY > 80) closeAll();
            startY = 0;
            deltaY = 0;
        };
        sheet.addEventListener('pointerup', finish);
        sheet.addEventListener('pointercancel', finish);
    });
}

function initFeaturedStack() {
    $$('[data-featured-stack]').forEach((stack) => {
        const cards = $$('[data-stack-card]', stack);
        const controls = $('[data-stack-controls]', stack);
        const previousButton = $('[data-stack-prev]', stack);
        const nextButton = $('[data-stack-next]', stack);
        const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
        const interval = 7500;
        let current = 0;
        let timer = 0;
        let paused = false;

        const render = () => cards.forEach((card, index) => {
            const position = (index - current + cards.length) % cards.length;
            const visible = position < Math.min(3, cards.length);
            card.dataset.pos = String(position);
            card.style.setProperty('--pos', String(position));
            card.setAttribute('aria-hidden', String(!visible));
            card.tabIndex = visible ? 0 : -1;
        });
        const restartProgress = () => {
            stack.classList.remove('is-playing');
            void stack.offsetWidth;
            if (!reducedMotion.matches && cards.length > 1) stack.classList.add('is-playing');
        };
        const schedule = () => {
            clearTimeout(timer);
            if (paused || reducedMotion.matches || cards.length < 2) return;
            timer = window.setTimeout(() => cycle(1, false), interval);
        };
        const cycle = (direction, manual = true) => {
            const leaving = cards[current];
            const nextIndex = (current + direction + cards.length) % cards.length;
            const arriving = direction > 0 ? cards[(nextIndex + 2) % cards.length] : cards[nextIndex];
            if (manual) stack.classList.add('is-manual');
            stack.classList.add('is-cycling');
            if (direction > 0) leaving?.classList.add('is-leaving');
            arriving?.classList.add('is-arriving');
            current = nextIndex;
            render();
            requestAnimationFrame(() => requestAnimationFrame(() => arriving?.classList.remove('is-arriving')));
            window.setTimeout(() => {
                leaving?.classList.remove('is-leaving');
                stack.classList.remove('is-cycling','is-manual');
            }, manual ? 300 : 520);
            restartProgress();
            schedule();
        };
        const pause = () => { paused = true; clearTimeout(timer); };
        const resume = () => { paused = false; restartProgress(); schedule(); };

        render();
        if (cards.length < 2) {
            if (controls) controls.hidden = true;
            return;
        }
        restartProgress();
        schedule();
        previousButton?.addEventListener('click', () => cycle(-1));
        nextButton?.addEventListener('click', () => cycle(1));
        stack.addEventListener('mouseenter', pause);
        stack.addEventListener('mouseleave', resume);
        stack.addEventListener('focusin', pause);
        stack.addEventListener('focusout', (event) => { if (!stack.contains(event.relatedTarget)) resume(); });
        document.addEventListener('visibilitychange', () => document.hidden ? pause() : resume());
        reducedMotion.addEventListener?.('change', () => {
            if (reducedMotion.matches) {
                stack.classList.remove('is-playing');
                clearTimeout(timer);
            } else {
                restartProgress();
                schedule();
            }
        });
    });
}

function initLoadMore() {
    $$('[data-load-more]').forEach((button) => {
        button.addEventListener('click', async () => {
            const nextUrl = button.dataset.nextPage;
            if (!nextUrl || button.disabled) return;
            button.disabled = true;
            button.textContent = translated('loading', 'Loading…');
            try {
                const response = await fetch(nextUrl);
                if (!response.ok) throw new Error('Unable to load posts');
                const documentNext = new DOMParser().parseFromString(await response.text(), 'text/html');
                const feed = $('[data-post-feed]');
                $$('[data-post-feed] .post-card', documentNext).forEach((card) => feed?.append(card));
                const nextButton = $('[data-load-more]', documentNext);
                if (nextButton?.dataset.nextPage) {
                    button.dataset.nextPage = nextButton.dataset.nextPage;
                    button.disabled = false;
                    button.textContent = translated('loadMore', 'Load More');
                } else {
                    button.parentElement?.remove();
                }
            } catch (error) {
                button.disabled = false;
                button.textContent = translated('tryAgain', 'Try Again');
            }
        });
    });
}

function initPricing() {
    $$('[data-pricing]').forEach((pricing) => {
        const controls = $$('input[name="billing-interval"]', pricing);
        const applyInterval = (interval) => {
            $$('.pricing-tier', pricing).forEach((tier) => {
                ['.pricing-price [data-pricing-interval]', '.pricing-actions [data-pricing-interval]'].forEach((selector) => {
                    const options = $$(selector, tier);
                    const selected = options.find((option) => option.dataset.pricingInterval === interval) || options[0];
                    options.forEach((option) => { option.hidden = option !== selected; });
                });
            });
        };

        controls.forEach((control) => control.addEventListener('change', () => {
            if (control.checked) applyInterval(control.value);
        }));
        applyInterval(controls.find((control) => control.checked)?.value || 'monthly');
    });
}

function initContactForms() {
    $$('[data-contact-form]').forEach((form) => {
        form.addEventListener('submit', async (event) => {
            event.preventDefault();
            const endpoint = form.getAttribute('action')?.trim();
            if (!endpoint || form.classList.contains('is-loading')) return;

            const button = $('button[type="submit"]', form);
            form.classList.add('is-loading');
            form.classList.remove('is-success', 'is-error');
            form.setAttribute('aria-busy', 'true');
            if (button) button.disabled = true;

            try {
                const response = await fetch(endpoint, {
                    method: form.method || 'POST',
                    headers: {
                        Accept: 'application/json'
                    },
                    body: new FormData(form)
                });
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                form.reset();
                form.classList.add('is-success');
            } catch {
                form.classList.add('is-error');
            } finally {
                form.classList.remove('is-loading');
                form.removeAttribute('aria-busy');
                if (button) button.disabled = false;
            }
        });
    });
}

function initContentMedia() {
    $$('[data-article-content],[data-standard-content]').forEach((content) => {
        $$('.kg-gallery-image', content).forEach((cell) => {
            const image = $('img', cell);
            const updateRatio = () => {
                const width = Number.parseFloat(image?.getAttribute('width')) || image?.naturalWidth;
                const height = Number.parseFloat(image?.getAttribute('height')) || image?.naturalHeight;
                if (width > 0 && height > 0) cell.style.flex = `${width / height} 1 0%`;
            };
            updateRatio();
            if (image && !image.complete) image.addEventListener('load', updateRatio, {once:true});
        });

        $$('.kg-image-card>img,.kg-gallery-image>img', content).forEach((image) => {
            if (image.closest('a') || image.dataset.lightboxReady) return;
            image.dataset.lightboxReady = 'true';
            image.tabIndex = 0;
            image.setAttribute('role', 'button');
            const openLabel = translated('openImageWithAlt', 'Open image: {alt}').replace('{alt}', image.alt || '');
            image.setAttribute('aria-label', image.alt ? openLabel : translated('openImage', 'Open image'));
            image.addEventListener('click', () => openLightbox(image));
            image.addEventListener('keydown', (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    openLightbox(image);
                }
            });
        });
    });
}

function initReadingTools() {
    const article = $('[data-article]');
    const content = $('[data-article-content]');
    const progress = $('[data-reading-progress]');

    if (!article || !content) return;

    const updateProgress = () => {
        if (!progress) return;
        const start = article.offsetTop;
        const distance = Math.max(1, article.offsetHeight - window.innerHeight);
        const percentage = Math.min(100, Math.max(0, ((window.scrollY - start) / distance) * 100));
        progress.style.width = `${percentage}%`;
    };
    addEventListener('scroll', updateProgress, {passive: true});
    updateProgress();

    const tocTargets = $$('[data-toc]');
    if (tocTargets.length) {
        const headings = $$('h2,h3', content);
        tocTargets.forEach((toc) => {
            const list = document.createElement('ol');
            headings.forEach((heading, index) => {
                if (!heading.id) heading.id = `section-${index + 1}`;
                const item = document.createElement('li');
                const link = document.createElement('a');
                link.href = `#${heading.id}`;
                link.textContent = heading.textContent;
                item.className = heading.tagName === 'H3' ? 'toc-subitem' : '';
                item.append(link);
                list.append(item);
            });
            toc.append(list);
        });
        if (headings.length) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    tocTargets.forEach((toc) => $$('a', toc).forEach((link) => link.classList.toggle('is-active', link.hash === `#${entry.target.id}`)));
                });
            }, {rootMargin: '-20% 0px -70%'});
            headings.forEach((heading) => observer.observe(heading));
        }
    }

}

function openLightbox(image) {
    const dialog = document.createElement('dialog');
    const closeButton = document.createElement('button');
    const preview = document.createElement('img');
    dialog.className = 'image-lightbox';
    dialog.setAttribute('aria-label', image.alt || translated('imagePreview', 'Image preview'));
    closeButton.type = 'button';
    closeButton.setAttribute('aria-label', translated('close', 'Close'));
    closeButton.textContent = '×';
    preview.src = image.currentSrc || image.src;
    preview.alt = image.alt || '';
    dialog.append(closeButton, preview);
    document.body.append(dialog);
    dialog.showModal();
    closeButton.focus();
    closeButton.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    dialog.addEventListener('close', () => {
        dialog.remove();
        image.focus({preventScroll:true});
    });
}

function initShare() {
    const dialog = $('[data-share-dialog]');
    if (!dialog) return;
    $$('[data-share-open]').forEach((button) => button.addEventListener('click', () => dialog.showModal()));
    $('[data-share-close]', dialog)?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });
    $('[data-copy-url]', dialog)?.addEventListener('click', async (event) => {
        await navigator.clipboard.writeText(location.href);
        event.currentTarget.textContent = translated('copied', 'Copied');
    });
}

function initPostTools() {
    const tocShell = $('[data-toc-shell]');
    const tocToggle = $('[data-toc-toggle]');
    const tocPanel = $('[data-toc-panel]');
    let tocStartY = 0;
    let tocDeltaY = 0;
    let tocPreviousFocus = null;

    const closeToc = () => {
        if (!tocShell) return;
        tocShell.classList.remove('is-open');
        tocToggle?.setAttribute('aria-expanded', 'false');
        tocPanel?.setAttribute('aria-hidden', 'true');
        tocPanel?.style.removeProperty('transform');
        document.body.classList.remove('sheet-open');
        tocPreviousFocus?.focus?.();
        tocPreviousFocus = null;
    };
    tocToggle?.addEventListener('click', () => {
        const open = !tocShell.classList.contains('is-open');
        if (!open) return closeToc();
        tocPreviousFocus = tocToggle;
        tocShell.classList.add('is-open');
        tocToggle.setAttribute('aria-expanded', 'true');
        tocPanel?.setAttribute('aria-hidden', 'false');
        if (matchMedia('(max-width:599.98px)').matches) document.body.classList.add('sheet-open');
        $('[data-toc-close]', tocPanel || document)?.focus();
    });
    $('[data-toc-close]')?.addEventListener('click', closeToc);
    $$('[data-toc] a').forEach((link) => link.addEventListener('click', closeToc));
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeToc(); });

    tocPanel?.addEventListener('pointerdown', (event) => {
        if (!matchMedia('(max-width:599.98px)').matches || !event.target.closest('.post-toc-head')) return;
        tocStartY = event.clientY;
        tocDeltaY = 0;
        tocPanel.setPointerCapture?.(event.pointerId);
    });
    tocPanel?.addEventListener('pointermove', (event) => {
        if (!tocStartY) return;
        tocDeltaY = Math.max(0, event.clientY - tocStartY);
        tocPanel.style.transform = `translateY(${tocDeltaY}px)`;
    });
    const finishTocDrag = () => {
        if (!tocStartY) return;
        tocPanel.style.removeProperty('transform');
        if (tocDeltaY > 80) closeToc();
        tocStartY = 0;
        tocDeltaY = 0;
    };
    tocPanel?.addEventListener('pointerup', finishTocDrag);
    tocPanel?.addEventListener('pointercancel', finishTocDrag);

    $('[data-comments-open]')?.addEventListener('click', () => {
        const comments = $('[data-comments-panel]');
        if (!comments) return;
        comments.hidden = false;
        comments.scrollIntoView({behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start'});
    });

    const videoSlot = $('[data-video-slot]');
    const firstVideo = $('[data-article-content] .kg-video-card');
    if (videoSlot && firstVideo) {
        videoSlot.append(firstVideo);
    }
}

function initTagOverflow() {
    const filter = $('[data-tag-filter]');
    const more = $('[data-tag-more]', filter || document);
    const panel = $('[data-tag-more-panel]', more || document);
    const tags = $$('[data-tag-priority]', filter || document);
    if (!filter || !more || !panel || !tags.length) return;

    const reset = () => {
        more.open = false;
        tags.forEach((tag) => filter.insertBefore(tag, more));
        more.hidden = true;
    };
    const fit = () => {
        reset();
        const overflowed = [];
        while (filter.scrollWidth > filter.clientWidth && tags.length > overflowed.length) {
            const tag = tags[tags.length - overflowed.length - 1];
            overflowed.unshift(tag);
            panel.prepend(tag);
            more.hidden = false;
        }
        if (!overflowed.length) more.open = false;
    };
    const close = () => { more.open = false; };
    const scheduleFit = () => requestAnimationFrame(fit);

    fit();
    addEventListener('resize', scheduleFit, {passive:true});
    more.addEventListener('toggle', () => { if (!more.open) close(); });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });
}

function initTagsLetterIndex() {
    $$('[data-tags-ledger]').forEach((ledger) => {
        const strip = $('[data-tags-letter-strip]');
        const entries = $$('[data-tags-index-entry]', ledger);
        if (!strip || !entries.length) return;

        const getIndexLetter = (name) => {
            const character = [...name.trim()][0];
            if (!character) return null;
            const latin = character.toLocaleUpperCase();
            if (/^[A-Z]$/.test(latin)) return latin;
            if (!/[\u4E00-\u9FFF]/.test(character)) return null;
            const codePoint = character.charCodeAt(0);
            const pinyinInitial = pinyinFirstLetter.all.charAt(codePoint - 19968).toUpperCase();
            return /^[A-Z]$/.test(pinyinInitial) ? pinyinInitial : null;
        };

        const indexedEntries = entries.map((entry) => {
            const name = $('[data-tags-index-name]', entry)?.textContent.trim() || '';
            return {entry, name, letter: getIndexLetter(name) || '#'};
        }).sort((left, right) => {
            const leftLetter = left.letter === '#' ? '{' : left.letter;
            const rightLetter = right.letter === '#' ? '{' : right.letter;
            return leftLetter.localeCompare(rightLetter) || left.name.localeCompare(right.name, 'zh-Hans-CN');
        });

        const groups = new Map();
        indexedEntries.forEach(({entry, letter}) => {
            const group = groups.get(letter) || [];
            group.push(entry);
            groups.set(letter, group);
        });

        if (groups.size < 2) {
            indexedEntries.forEach(({entry}) => ledger.append(entry));
            return;
        }

        const fragment = document.createDocumentFragment();
        groups.forEach((groupEntries, letter) => {
            const id = `tag-letter-${letter === '#' ? 'misc' : letter.toLowerCase()}`;
            const section = document.createElement('section');
            section.className = 'tags-ledger-group';
            section.id = id;

            const heading = document.createElement('h2');
            heading.className = 'tags-ledger-group-title';
            heading.textContent = letter;

            const list = document.createElement('div');
            list.className = 'tags-ledger-group-entries';
            groupEntries.forEach((entry) => list.append(entry));
            section.append(heading, list);
            fragment.append(section);

            const link = document.createElement('a');
            link.href = `#${id}`;
            link.textContent = letter;
            strip.append(link);
        });

        ledger.replaceChildren(fragment);
        ledger.classList.add('is-grouped');
        strip.hidden = false;
    });
}

function initFooterLinks() {
    const isSafeFooterUrl = (value) => {
        if (typeof value !== 'string' || !value.trim() || value.startsWith('//')) return null;
        if (value.startsWith('/') || value.startsWith('#')) return new URL(value, location.origin);
        try {
            const url = new URL(value);
            return url.protocol === 'http:' || url.protocol === 'https:' ? url : null;
        } catch {
            return null;
        }
    };

    $$('[data-footer-links]').forEach((column) => {
        const raw = column.dataset.footerLinksJson?.trim();
        if (!raw) return;
        try {
            const links = JSON.parse(raw).map(({label, url}) => ({
                label: typeof label === 'string' ? label.trim() : '',
                url: isSafeFooterUrl(url)
            })).filter(({label, url}) => label && url);
            if (!links.length) return;
            $$('a', column).forEach((link) => link.remove());
            links.forEach(({label, url}) => {
                const link = document.createElement('a');
                link.href = url.href;
                link.textContent = label;
                if (url.origin !== location.origin) {
                    link.target = '_blank';
                    link.rel = 'noopener';
                }
                column.append(link);
            });
        } catch {
            console.warn('Senna footer links must be a valid JSON array. Using the default links instead.');
        }
    });
}

initSheets();
initFeaturedStack();
initLoadMore();
initPricing();
initContactForms();
initContentMedia();
initReadingTools();
initShare();
initPostTools();
initTagOverflow();
initTagsLetterIndex();
initFooterLinks();
